import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { db } from "../firebase/config";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";

function fmt(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}

function formatMonth(key) {
  if (!key) return "";
  const [y, m] = key.split("-");
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${monthNames[parseInt(m, 10) - 1]}-${y}`;
}

// ══════════════════════════════════════════════════════════════
// CATEGORY LOOKUP — maps each notification "type" to one of the
// 4 category bells (Payments / Polls & Elections / Tasks & Events /
// General). Any type NOT listed here safely falls back to "General"
// so nothing ever gets lost or hidden.
//
// To add a new type later: just add one line below. No other file
// needs to change.
// ══════════════════════════════════════════════════════════════
const TYPE_TO_CATEGORY = {
  // Payments
  dues_reminder:               "Payments",
  debit_tanker_order_paid:     "Payments",
  tanker_retroactive_auto:     "Payments",
  debit_auto_recorded:         "Payments",
  tanker_order_paid_via_gmail: "Payments",
  tanker_retroactive_entry:    "Payments",
  expense_corrected:           "Payments",
  payment_confirmed:           "Payments",
  payment_declared:            "Payments",
  unmatched_credit:            "Payments",
  water_bill_ready:            "Payments",

  // Tasks & Events
  adhoc_created:               "Tasks & Events",
  event_assigned:               "Tasks & Events",
  event_single_assignee:        "Tasks & Events",
  event_created:                "Tasks & Events",
  event_update:                  "Tasks & Events",
  event_query:                   "Tasks & Events",
  event_reply:                   "Tasks & Events",
  event_due_updated:             "Tasks & Events",
  meeting_reminder:              "Tasks & Events",

  // General
  tenant_added:                 "General",
  tenant_exited:                "General",
  ownership_transfer_pending:   "General",
  ownership_transfer_approved:  "General",
  rep_nomination:                "General",
};

function getCategory(type) {
  return TYPE_TO_CATEGORY[type] || "General";
}

// Some older notifications (from gmailService.js) were saved with a
// field called "read" instead of "isRead". This helper checks both,
// so nothing is stuck showing as permanently unread by mistake.
function isNotificationRead(n) {
  if (typeof n.isRead === "boolean") return n.isRead;
  if (typeof n.read === "boolean") return n.read;
  return false;
}

// Color choices offered in the "Add Category" form. Stored as a
// simple word (e.g. "red") on the category document, and turned
// into an actual hex color here whenever we need to display it.
const COLOR_OPTIONS = [
  { name: "red",    hex: "#ff5252" },
  { name: "yellow", hex: "#ffca28" },
  { name: "green",  hex: "#4caf50" },
  { name: "blue",   hex: "#42a5f5" },
  { name: "purple", hex: "#ab47bc" },
  { name: "orange", hex: "#ffa726" },
];
function colorHex(colorName) {
  const found = COLOR_OPTIONS.find(c => c.name === colorName);
  return found ? found.hex : "#8888a8";
}

// ══════════════════════════════════════════════════════════════
// NotificationBell
// Props:
//   user        — logged-in user object (from AuthContext)
//   duesData    — array of pending dues passed from Dashboard
//                 each item: { type, label, month, amount }
//                 empty array [] if no dues or user is MC/Watchman
// ══════════════════════════════════════════════════════════════
export default function NotificationBell({ user, duesData = [] }) {
  const [notifications, setNotifications]   = useState([]);
  const [categories,    setCategories]      = useState([]); // from Firestore: notificationCategories
  const [bellOpen,      setBellOpen]         = useState(false);
  const [activeCategory, setActiveCategory] = useState(null); // null = showing 4 rows, else showing that category's list
  const [showDuesPopup, setShowDuesPopup]    = useState(false);

  // ── Manage Categories (President only) — add/delete state ────
  const [showAddForm,      setShowAddForm]      = useState(false);
  const [newCatName,       setNewCatName]       = useState("");
  const [newCatIcon,       setNewCatIcon]       = useState("");
  const [newCatColor,      setNewCatColor]      = useState("blue");
  const [formError,        setFormError]        = useState("");
  const [savingCategory,   setSavingCategory]   = useState(false);
  const [deleteConfirmId,  setDeleteConfirmId]  = useState(null);

  const userUid = user?.id || user?.uid;
  const userRole = user?.role || null; // e.g. "President", "Secretary", "Treasurer"

  // ── Fetch notifications from Firestore ──────────────────────
  // Fetches 3 kinds: (1) sent directly to this person (toUid),
  // (2) sent to everyone (toUid: "all"), and (3) sent to this
  // person's MC role (forRoles array-contains, e.g. gmailService.js
  // notifications meant for "President"/"Treasurer"). Previously,
  // (3) was never fetched, so role-based notifications from Gmail
  // sync were silently invisible in the bell — this fixes that.
  async function fetchNotifications() {
    if (!userUid) return;
    try {
      const queries = [
        getDocs(query(collection(db, "notifications"), where("toUid", "==", userUid))),
        getDocs(query(collection(db, "notifications"), where("toUid", "==", "all"))),
      ];
      if (userRole) {
        queries.push(
          getDocs(query(collection(db, "notifications"), where("forRoles", "array-contains", userRole)))
        );
      }

      const snaps = await Promise.all(queries);
      const combined = snaps.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));

      // De-duplicate (a doc could theoretically match more than one query)
      const seen = new Set();
      const deduped = combined.filter(n => {
        if (seen.has(n.id)) return false;
        seen.add(n.id);
        return true;
      });

      // Category is resolved dynamically (see resolveCategory below),
      // not stored permanently — this way, if a category is ever
      // deleted, its notifications automatically show under General
      // on the very next render, with no extra cleanup step needed.
      deduped.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setNotifications(deduped);
    } catch (e) {
      console.error("Notification fetch error:", e);
    }
  }

  // ── Fetch the 4 (or more) categories from Firestore ──────────
  // These were seeded once via seedNotificationCategories.mjs and
  // can be added/edited/deleted later from an admin screen — this
  // file just reads whatever is there, sorted by "order".
  async function fetchCategories() {
    try {
      const snap = await getDocs(collection(db, "notificationCategories"));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(list);
    } catch (e) {
      console.error("Category fetch error:", e);
    }
  }

  // ── On mount — fetch notifications and categories ────────────
  useEffect(() => {
    fetchNotifications();
    fetchCategories();
  }, [userUid]);

  // ── When duesData arrives from Dashboard, show popup ─────────
  useEffect(() => {
    if (duesData.length > 0) {
      setShowDuesPopup(true);
    } else if (duesData.length === 0 && userUid) {
      // Dues cleared (resident paid) — auto-remove dues_reminder notifications
      (async () => {
        try {
          const snap = await getDocs(
            query(collection(db, "notifications"),
              where("toUid", "==", userUid),
              where("type", "==", "dues_reminder"))
          );
          for (const d of snap.docs) {
            await deleteDoc(doc(db, "notifications", d.id));
          }
          setNotifications(prev => prev.filter(n => n.type !== "dues_reminder"));
        } catch (e) { /* silent */ }
      })();
    }
  }, [duesData]);

  // ── Write a single notification to Firestore ────────────────
  async function writeNotification({ toUid, message, type }) {
    if (!toUid) return;
    try {
      await addDoc(collection(db, "notifications"), {
        toUid, message, type,
        isRead: false,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("Notification write error:", e);
    }
  }

  // ── Mark all as read when bell opens ────────────────────────
  async function markAllRead() {
    const unread = notifications.filter(n => !isNotificationRead(n));
    for (const n of unread) {
      try {
        // Always write isRead going forward — this is the field
        // isNotificationRead() checks first, so old "read"-based
        // records get correctly upgraded the first time they're seen.
        await updateDoc(doc(db, "notifications", n.id), { isRead: true });
      } catch (e) { /* silent */ }
    }
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  }

  // ── Handle dues popup dismiss → save to bell ─────────────────
  async function handleDuesPopupClose() {
    setShowDuesPopup(false);
    if (!userUid || duesData.length === 0) return;

    // Fetch existing dues_reminder notifications for this user
    let existing = [];
    try {
      const snap = await getDocs(
        query(
          collection(db, "notifications"),
          where("toUid", "==", userUid),
          where("type", "==", "dues_reminder")
        )
      );
      existing = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) { /* silent */ }

    for (const due of duesData) {
      const message = `${due.label} due for ${formatMonth(due.month)} — ${fmt(due.amount)}. Please pay.`;
      const matches = existing.filter(n => n.message === message);
      if (matches.length > 0) {
        // Already exists — just update the timestamp, delete any extras
        try {
          await updateDoc(doc(db, "notifications", matches[0].id), { createdAt: serverTimestamp() });
        } catch (e) { /* silent */ }
        for (let i = 1; i < matches.length; i++) {
          try { await deleteDoc(doc(db, "notifications", matches[i].id)); } catch (e) { /* silent */ }
        }
      } else {
        // New due — add fresh
        await writeNotification({ toUid: userUid, message, type: "dues_reminder" });
      }
    }

    // Remove stale dues_reminder notifications that are no longer pending (e.g. already paid)
    const currentMessages = duesData.map(
      due => `${due.label} due for ${formatMonth(due.month)} — ${fmt(due.amount)}. Please pay.`
    );
    for (const n of existing) {
      if (!currentMessages.includes(n.message)) {
        try { await deleteDoc(doc(db, "notifications", n.id)); } catch (e) { /* silent */ }
      }
    }

    // Refresh bell
    await fetchNotifications();
  }

  // ── Bell toggle ──────────────────────────────────────────────
  async function handleBellClick() {
    const opening = !bellOpen;
    setBellOpen(opening);
    if (opening) {
      setActiveCategory(null); // always start at the 4-category view
      await fetchNotifications();
      // Note: we no longer mark everything read the instant the bell
      // opens — only once the person actually taps into a category
      // and sees its list (see handleCategoryClick below).
    }
  }

  // ── Work out which category a notification belongs to, live ──
  // Uses the TYPE_TO_CATEGORY lookup, but falls back to "General"
  // if that category no longer exists (e.g. President deleted it).
  function resolveCategory(type) {
    const mapped = getCategory(type);
    const validNames = categories.map(c => c.name);
    return validNames.includes(mapped) ? mapped : "General";
  }

  // ── Tapping a category row — open its filtered list, mark those read
  async function handleCategoryClick(categoryName) {
    setActiveCategory(categoryName);
    const toMark = notifications.filter(
      n => resolveCategory(n.type) === categoryName && !isNotificationRead(n)
    );
    for (const n of toMark) {
      try {
        await updateDoc(doc(db, "notifications", n.id), { isRead: true });
      } catch (e) { /* silent */ }
    }
    if (toMark.length > 0) {
      setNotifications(prev => prev.map(n =>
        resolveCategory(n.type) === categoryName ? { ...n, isRead: true } : n
      ));
    }
  }

  const unreadCount = notifications.filter(n => !isNotificationRead(n)).length;
  const hasUnread   = unreadCount > 0;

  // Group notifications by category, and count unread ones per category
  function countUnreadInCategory(categoryName) {
    return notifications.filter(n => resolveCategory(n.type) === categoryName && !isNotificationRead(n)).length;
  }
  function notificationsInCategory(categoryName) {
    return notifications.filter(n => resolveCategory(n.type) === categoryName);
  }

  // ── Manage Categories: add a new category ────────────────────
  async function handleAddCategory() {
    const name = newCatName.trim();
    if (!name) { setFormError("Please enter a name."); return; }
    if (categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      setFormError("A category with this name already exists.");
      return;
    }
    setSavingCategory(true);
    setFormError("");
    try {
      const maxOrder = categories.reduce((max, c) => Math.max(max, c.order || 0), 0);
      await addDoc(collection(db, "notificationCategories"), {
        name,
        icon: newCatIcon.trim() || "🔔",
        color: newCatColor,
        order: maxOrder + 1,
      });
      setNewCatName(""); setNewCatIcon(""); setNewCatColor("blue");
      setShowAddForm(false);
      await fetchCategories();
    } catch (e) {
      console.error("Add category error:", e);
      setFormError("Something went wrong saving this. Please try again.");
    } finally {
      setSavingCategory(false);
    }
  }

  // ── Manage Categories: delete a category ─────────────────────
  // No separate cleanup step needed — since category membership is
  // worked out live via resolveCategory(), any notifications that
  // were under this category simply show under General from now on.
  async function handleDeleteCategory(cat) {
    try {
      await deleteDoc(doc(db, "notificationCategories", cat.id));
      setDeleteConfirmId(null);
      await fetchCategories();
    } catch (e) {
      console.error("Delete category error:", e);
    }
  }

  // ── Leaving the Manage Categories screen — reset its form state
  function closeManageScreen() {
    setActiveCategory(null);
    setShowAddForm(false);
    setDeleteConfirmId(null);
    setFormError("");
    setNewCatName(""); setNewCatIcon(""); setNewCatColor("blue");
  }

  return (
    <>
      {/* ── Bell Button ── */}
      <div style={{ position: "relative" }}>
        <button
          onClick={handleBellClick}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: "20px", padding: "4px", lineHeight: 1,
            position: "relative",
          }}
          title="Notifications"
        >
          🔔
          {unreadCount > 0 && (
            <span style={{
              position: "absolute", top: "-4px", right: "-6px",
              minWidth: "16px", height: "16px", borderRadius: "8px",
              background: "#ff5252", border: "1px solid #0f0f13",
              color: "#fff", fontSize: "9px", fontWeight: "700",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "0 3px",
            }}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
          {unreadCount === 0 && notifications.length > 0 && (
            <span style={{
              position: "absolute", top: "0px", right: "0px",
              width: "8px", height: "8px", borderRadius: "50%",
              background: "#4caf50", border: "1px solid #0f0f13",
            }} />
          )}
        </button>
      </div>

      {/* ── Click-outside overlay + Panel — rendered via Portal directly on body ── */}
      {bellOpen && createPortal(
        <>
          {/* Overlay */}
          <div
            onClick={() => setBellOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 998 }}
          />
          {/* Panel */}
          <div
            style={{
              position: "fixed", top: "52px", right: "12px",
              width: "300px",
              background: "#1a1a24", border: "1px solid #2e2e40",
              borderRadius: "12px", boxShadow: "0 8px 32px #00000060",
              zIndex: 999,
            }}
          >
            {/* Panel Header */}
            <div style={{
              padding: "10px 14px", borderBottom: "1px solid #2e2e40",
              fontSize: "12px", fontWeight: "700", color: "#e8e8f0",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              flexShrink: 0,
            }}>
              {activeCategory ? (
                <button
                  onClick={closeManageScreen}
                  style={{
                    background: "none", border: "none", color: "#e8e8f0",
                    cursor: "pointer", fontSize: "12px", fontWeight: "700",
                    display: "flex", alignItems: "center", gap: "6px", padding: 0,
                  }}
                >
                  ← {activeCategory === "__manage__" ? "Manage Categories" : activeCategory}
                </button>
              ) : (
                <span>
                  Notifications{" "}
                  {unreadCount > 0 && <span style={{ color: "#ff5252" }}>({unreadCount} new)</span>}
                </span>
              )}
              <button
                onClick={() => { setBellOpen(false); closeManageScreen(); }}
                style={{ background: "none", border: "none", color: "#555570", cursor: "pointer", fontSize: "14px" }}
              >✕</button>
            </div>

            {/* Scrollable Body — either the 4 category rows, or one category's filtered list */}
            <div
              style={{
                overflowY: "auto",
                height: "370px",
                scrollbarWidth: "thin",
                scrollbarColor: "#6c63ff #1a1a24",
              }}
            >
              {!activeCategory ? (
                // ── 4 (or more) category rows ──
                categories.length === 0 ? (
                  <div style={{ padding: "24px", textAlign: "center", color: "#555570", fontSize: "12px" }}>
                    No categories set up yet
                  </div>
                ) : (
                  <>
                    {categories.map(cat => {
                      const count = countUnreadInCategory(cat.name);
                      const total = notificationsInCategory(cat.name).length;
                      return (
                        <div
                          key={cat.id}
                          onClick={() => handleCategoryClick(cat.name)}
                          style={{
                            padding: "12px 14px", borderBottom: "1px solid #1e1e2e",
                            display: "flex", alignItems: "center", gap: "12px",
                            cursor: "pointer",
                          }}
                        >
                          <span style={{
                            fontSize: "20px", width: "36px", height: "36px",
                            borderRadius: "10px", background: colorHex(cat.color) + "33",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>
                            {cat.icon || "🔔"}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>
                              {cat.name}
                            </div>
                            <div style={{ fontSize: "10px", color: "#8888a8", marginTop: "2px" }}>
                              {total === 0 ? "Nothing here yet" : `${total} total`}
                            </div>
                          </div>
                          {count > 0 && (
                            <span style={{
                              minWidth: "20px", height: "20px", borderRadius: "10px",
                              background: "#ff5252", color: "#fff", fontSize: "11px",
                              fontWeight: "700", display: "flex", alignItems: "center",
                              justifyContent: "center", padding: "0 6px", flexShrink: 0,
                            }}>
                              {count > 9 ? "9+" : count}
                            </span>
                          )}
                          <span style={{ color: "#555570", fontSize: "14px", flexShrink: 0 }}>›</span>
                        </div>
                      );
                    })}

                    {/* 5th row — President-only entry point to Manage Categories.
                        Full add/edit/delete screen is parked for a future release. */}
                    {userRole === "President" && (
                      <div
                        onClick={() => setActiveCategory("__manage__")}
                        style={{
                          padding: "12px 14px", borderTop: "1px solid #2e2e40",
                          display: "flex", alignItems: "center", gap: "12px",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{
                          fontSize: "20px", width: "36px", height: "36px",
                          borderRadius: "10px", background: "#ffffff10",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          ⚙️
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>
                            Manage Categories
                          </div>
                          <div style={{ fontSize: "10px", color: "#8888a8", marginTop: "2px" }}>
                            President only
                          </div>
                        </div>
                        <span style={{ color: "#555570", fontSize: "14px", flexShrink: 0 }}>›</span>
                      </div>
                    )}
                  </>
                )
              ) : activeCategory === "__manage__" ? (
                // ── Manage Categories — real add / delete screen ──
                <div style={{ padding: "14px" }}>
                  {categories.map(cat => (
                    <div key={cat.id} style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      padding: "10px 0", borderBottom: "1px solid #1e1e2e",
                    }}>
                      <span style={{
                        fontSize: "16px", width: "30px", height: "30px",
                        borderRadius: "8px", background: colorHex(cat.color) + "33",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {cat.icon || "🔔"}
                      </span>
                      <div style={{ flex: 1, fontSize: "12px", color: "#e8e8f0", minWidth: 0 }}>
                        {cat.name}
                      </div>

                      {deleteConfirmId === cat.id ? (
                        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
                          <span style={{ fontSize: "10px", color: "#8888a8" }}>Delete?</span>
                          <button
                            onClick={() => handleDeleteCategory(cat)}
                            style={{
                              background: "#ff5252", border: "none", color: "#fff",
                              borderRadius: "6px", padding: "4px 8px", fontSize: "11px",
                              fontWeight: "700", cursor: "pointer",
                            }}
                          >Yes</button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            style={{
                              background: "none", border: "1px solid #2e2e40", color: "#8888a8",
                              borderRadius: "6px", padding: "4px 8px", fontSize: "11px", cursor: "pointer",
                            }}
                          >No</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(cat.id)}
                          title={`Delete ${cat.name}`}
                          style={{
                            background: "#ffffff10", border: "none", color: "#ff5252",
                            borderRadius: "6px", width: "26px", height: "26px",
                            fontSize: "16px", fontWeight: "700", cursor: "pointer",
                            flexShrink: 0, lineHeight: 1,
                          }}
                        >−</button>
                      )}
                    </div>
                  ))}

                  {/* + Add Category */}
                  {!showAddForm ? (
                    <button
                      onClick={() => setShowAddForm(true)}
                      style={{
                        marginTop: "14px", width: "100%", padding: "10px",
                        borderRadius: "8px", background: "#6c63ff", border: "none",
                        color: "#fff", fontSize: "13px", fontWeight: "700", cursor: "pointer",
                      }}
                    >+ Add Category</button>
                  ) : (
                    <div style={{
                      marginTop: "14px", padding: "12px", borderRadius: "10px",
                      background: "#0f0f13", border: "1px solid #2e2e40",
                    }}>
                      <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "6px" }}>Name</div>
                      <input
                        value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        placeholder="e.g. Maintenance Requests"
                        style={{
                          width: "100%", padding: "8px", borderRadius: "6px",
                          border: "1px solid #2e2e40", background: "#1a1a24",
                          color: "#e8e8f0", fontSize: "12px", marginBottom: "10px",
                          boxSizing: "border-box",
                        }}
                      />

                      <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "6px" }}>Icon (type an emoji)</div>
                      <input
                        value={newCatIcon}
                        onChange={e => setNewCatIcon(e.target.value)}
                        placeholder="e.g. 🔧"
                        style={{
                          width: "100%", padding: "8px", borderRadius: "6px",
                          border: "1px solid #2e2e40", background: "#1a1a24",
                          color: "#e8e8f0", fontSize: "12px", marginBottom: "10px",
                          boxSizing: "border-box",
                        }}
                      />

                      <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "6px" }}>Color</div>
                      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                        {COLOR_OPTIONS.map(c => (
                          <div
                            key={c.name}
                            onClick={() => setNewCatColor(c.name)}
                            style={{
                              width: "24px", height: "24px", borderRadius: "50%",
                              background: c.hex, cursor: "pointer",
                              border: newCatColor === c.name ? "2px solid #fff" : "2px solid transparent",
                            }}
                          />
                        ))}
                      </div>

                      {formError && (
                        <div style={{ fontSize: "11px", color: "#ff5252", marginBottom: "10px" }}>
                          {formError}
                        </div>
                      )}

                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={handleAddCategory}
                          disabled={savingCategory}
                          style={{
                            flex: 1, padding: "9px", borderRadius: "8px",
                            background: "#6c63ff", border: "none", color: "#fff",
                            fontSize: "12px", fontWeight: "700",
                            cursor: savingCategory ? "default" : "pointer",
                            opacity: savingCategory ? 0.7 : 1,
                          }}
                        >
                          {savingCategory ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={() => {
                            setShowAddForm(false);
                            setFormError("");
                            setNewCatName(""); setNewCatIcon(""); setNewCatColor("blue");
                          }}
                          style={{
                            flex: 1, padding: "9px", borderRadius: "8px",
                            background: "none", border: "1px solid #2e2e40",
                            color: "#8888a8", fontSize: "12px", cursor: "pointer",
                          }}
                        >Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // ── Filtered list for the active category ──
                notificationsInCategory(activeCategory).length === 0 ? (
                  <div style={{ padding: "24px", textAlign: "center", color: "#555570", fontSize: "12px" }}>
                    No notifications yet
                  </div>
                ) : (
                  notificationsInCategory(activeCategory).slice(0, 20).map(n => (
                    <div key={n.id} style={{
                      padding: "10px 14px", borderBottom: "1px solid #1e1e2e",
                      display: "flex", gap: "10px", alignItems: "flex-start",
                      background: isNotificationRead(n) ? "transparent" : "#6c63ff18",
                    }}>
                      <span style={{ fontSize: "16px", flexShrink: 0 }}>
                        {n.type === "payment_confirmed" ? "✅"
                          : n.type === "payment_declared" ? "💳"
                          : n.type === "water_bill_ready" ? "💧"
                          : n.type === "unmatched_credit" ? "⚠️"
                          : n.type === "dues_reminder" ? "🔴"
                          : "🔔"}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: "12px", color: "#e8e8f0", lineHeight: 1.4 }}>
                          {n.message}
                        </div>
                        {n.createdAt && (
                          <div style={{ fontSize: "10px", color: "#555570", marginTop: "3px" }}>
                            {new Date(n.createdAt.seconds * 1000).toLocaleDateString("en-IN")}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ── Dues Popup Modal ── */}
      {showDuesPopup && (
        <div style={{
          position: "fixed", inset: 0, background: "#00000080",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
          padding: "20px",
        }}>
          <div style={{
            background: "#1a1a24", border: "1px solid #2e2e40",
            borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "340px",
          }}>
            <div style={{ fontSize: "28px", textAlign: "center", marginBottom: "8px" }}>🔔</div>
            <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0", textAlign: "center", marginBottom: "4px" }}>
              Pending Dues
            </div>
            <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginBottom: "20px" }}>
              You have the following outstanding payments
            </div>

            {duesData.map((due, i) => (
              <div key={i} style={{
                background: "#0f0f13", border: "1px solid #2e2e40",
                borderRadius: "10px", padding: "12px 16px", marginBottom: "10px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{due.label}</div>
                  {due.month && (
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{formatMonth(due.month)}</div>
                  )}
                  {due.type === "adhoc" && (
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>Society Work — pay via Dashboard</div>
                  )}
                </div>
                <div style={{ fontSize: "16px", fontWeight: "700", color: "#ff5252" }}>{fmt(due.amount)}</div>
              </div>
            ))}

            <div style={{ fontSize: "11px", color: "#555570", textAlign: "center", marginBottom: "16px" }}>
              Please pay via the Dashboard. These reminders will be saved to your notification bell.
            </div>

            <button
              onClick={handleDuesPopupClose}
              style={{
                width: "100%", padding: "12px", borderRadius: "10px",
                background: "#6c63ff", border: "none", color: "#fff",
                fontSize: "13px", fontWeight: "700", cursor: "pointer",
              }}
            >
              Got it, I'll pay soon
            </button>
          </div>
        </div>
      )}
    </>
  );
}
